import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useEncryption } from '../context/EncryptionContext';
import { ArrowLeft, Lock, Shield, Key, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const EncryptionSettings = () => {
  const navigate = useNavigate();
  const { isInitialized, encryptionEnabled, toggleEncryption, hasKeys } = useEncryption();

  const handleToggle = () => {
    if (!isInitialized) {
      toast.error('Encryption not initialized. Please try again later.');
      return;
    }
    toggleEncryption(!encryptionEnabled);
    toast.success(encryptionEnabled ? 'End-to-End Encryption disabled' : 'End-to-End Encryption enabled');
  };

  return (
    <div className="min-h-screen bg-background-DEFAULT flex flex-col" data-testid="encryption-settings-page">
      <div className="p-6 border-b border-border-DEFAULT">
        <div className="chat-container flex items-center gap-4">
          <button
            data-testid="back-btn"
            onClick={() => navigate('/settings')}
            className="h-10 w-10 rounded-full flex items-center justify-center hover:bg-secondary-DEFAULT transition-colors"
          >
            <ArrowLeft size={20} className="text-text-primary" />
          </button>
          <h1 className="font-heading text-2xl text-text-primary">End-to-End Encryption</h1>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-hide p-6">
        <div className="chat-container max-w-2xl mx-auto space-y-6">
          {/* Status Card */}
          <div className={`rounded-3xl p-6 shadow-card border ${encryptionEnabled ? 'bg-green-50 border-green-200' : 'bg-white border-border-DEFAULT'}`}>
            <div className="flex items-center gap-4 mb-4">
              <div className={`w-14 h-14 rounded-full flex items-center justify-center ${encryptionEnabled ? 'bg-green-100' : 'bg-secondary-DEFAULT'}`}>
                {encryptionEnabled ? (
                  <Lock size={28} className="text-green-600" />
                ) : (
                  <Lock size={28} className="text-text-tertiary" />
                )}
              </div>
              <div className="flex-1">
                <h2 className="font-heading text-xl text-text-primary">
                  {encryptionEnabled ? 'Encryption Active' : 'Encryption Disabled'}
                </h2>
                <p className="text-sm text-text-secondary">
                  {encryptionEnabled 
                    ? 'Your messages are end-to-end encrypted' 
                    : 'Enable to encrypt your messages'}
                </p>
              </div>
            </div>

            <button
              data-testid="toggle-encryption-btn"
              onClick={handleToggle}
              className={`w-full py-3 rounded-xl font-medium transition-colors ${
                encryptionEnabled 
                  ? 'bg-red-100 text-red-600 hover:bg-red-200' 
                  : 'bg-primary text-white hover:bg-primary/90'
              }`}
            >
              {encryptionEnabled ? 'Disable Encryption' : 'Enable Encryption'}
            </button>
          </div>

          {/* How it Works */}
          <div className="bg-white rounded-3xl p-6 shadow-card border border-border-DEFAULT">
            <h2 className="font-heading text-lg text-text-primary mb-4">How it Works</h2>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Key size={16} className="text-primary" />
                </div>
                <div>
                  <h3 className="font-medium text-text-primary">Unique Keys</h3>
                  <p className="text-sm text-text-secondary">
                    A unique encryption key pair is generated for your device. Your private key never leaves your device.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Shield size={16} className="text-primary" />
                </div>
                <div>
                  <h3 className="font-medium text-text-primary">Encrypted Messages</h3>
                  <p className="text-sm text-text-secondary">
                    Messages are encrypted on your device before being sent. Only the recipient can decrypt them.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Lock size={16} className="text-primary" />
                </div>
                <div>
                  <h3 className="font-medium text-text-primary">No Server Access</h3>
                  <p className="text-sm text-text-secondary">
                    Even ChatBuzzz servers cannot read your encrypted messages. Only you and your recipient have the keys.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Key Status */}
          <div className="bg-white rounded-3xl p-6 shadow-card border border-border-DEFAULT">
            <h2 className="font-heading text-lg text-text-primary mb-4">Key Status</h2>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                <span className="text-text-primary">Encryption Keys</span>
                <div className="flex items-center gap-2">
                  {hasKeys ? (
                    <>
                      <CheckCircle size={18} className="text-green-500" />
                      <span className="text-green-600 text-sm">Generated</span>
                    </>
                  ) : (
                    <>
                      <AlertCircle size={18} className="text-yellow-500" />
                      <span className="text-yellow-600 text-sm">Generating...</span>
                    </>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                <span className="text-text-primary">Public Key Uploaded</span>
                <div className="flex items-center gap-2">
                  {isInitialized ? (
                    <>
                      <CheckCircle size={18} className="text-green-500" />
                      <span className="text-green-600 text-sm">Yes</span>
                    </>
                  ) : (
                    <>
                      <AlertCircle size={18} className="text-yellow-500" />
                      <span className="text-yellow-600 text-sm">Pending</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Warning */}
          <div className="bg-yellow-50 rounded-3xl p-6 border border-yellow-200">
            <div className="flex items-start gap-3">
              <AlertCircle size={20} className="text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-yellow-800">Important</h3>
                <p className="text-sm text-yellow-700 mt-1">
                  If you clear your browser data, you will lose your encryption keys and won't be able to decrypt old messages. 
                  Both you and your chat partner need encryption enabled for messages to be encrypted.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EncryptionSettings;
